import { useCallback, useRef } from 'react';

import * as Sentry from '@sentry/react';

import { vnextApiClient } from '@/services/vnext-api';
import {
  MonitoredSubjectPage,
  MonitoredSubjectQuery,
  MonitoringCancellationReason,
  SubjectCancellation,
  SubjectRegistration,
} from '@/types/monitoring';
import { toError } from '@/utils/errors';
import { UNCHANGED, usePoll, withEtag } from './usePoll';

/** Subjects change when someone enrols, suspends or cancels — not by the minute. */
export const MONITORED_SUBJECTS_POLL_MS = 60_000;

const EMPTY: MonitoredSubjectPage = { items: [], total: 0, version: '' };

/**
 * A page of monitored subjects, polled with ETag revalidation, and the action
 * that cancels one.
 *
 * The query is a `refetchDeps` entry, so a filter or page change drops the
 * stored ETag: a different filter is a different token space, and reusing the
 * token would 304 against the previous filter's rows.
 */
export function useMonitoredSubjects(query: MonitoredSubjectQuery) {
  const key = JSON.stringify(query);
  const queryRef = useRef(query);
  queryRef.current = query;

  const poll = usePoll<MonitoredSubjectPage>({
    interval: MONITORED_SUBJECTS_POLL_MS,
    refetchDeps: [key],
    queryFn: async ({ signal, etag, manual }) => {
      const result = await vnextApiClient.listMonitoredSubjects(queryRef.current, {
        signal,
        etag: manual ? null : etag,
      });
      return result === 'not-modified' ? UNCHANGED : withEtag(result.data, result.etag);
    },
    onError: (err) => Sentry.captureException(toError(err)),
  });
  const { refetch } = poll;

  const cancel = useCallback(
    async (
      subjectId: string,
      payload: { reason: MonitoringCancellationReason; note: string | null }
    ): Promise<SubjectCancellation> => {
      try {
        return await vnextApiClient.cancelMonitoredSubject(subjectId, payload);
      } finally {
        // Also on failure: a 409 means it was already cancelled, and the row
        // on screen is the stale one.
        await refetch();
      }
    },
    [refetch]
  );

  /** Register a pending subject now. Resolves with Castellum's answer, refusal included. */
  const register = useCallback(
    async (subjectId: string): Promise<SubjectRegistration> => {
      try {
        return await vnextApiClient.registerMonitoredSubject(subjectId);
      } finally {
        // Also on failure: a 409 means it is no longer pending.
        await refetch();
      }
    },
    [refetch]
  );

  return {
    page: poll.data ?? EMPTY,
    isLoading: poll.isLoading,
    error: poll.error === undefined ? null : toError(poll.error),
    refetch,
    cancel,
    register,
  };
}
